import type {
  AccountBeneficiaryFormData,
  ApplicationFormAccessor,
} from "$lib/yup"
import {
  accountBeneficiaryAdapter,
  beneficiaryAddressSchema,
  beneficiaryDetailsSchema,
  beneficiaryNameSchema,
} from "$lib/yup/account/add-beneficiary"
import { calculatePercentageRemainder } from "@tastyworks/shared-utils/math/precision"
import {
  ACCOUNT_BENEFICIARY_TYPES,
  AccountBeneficiary,
  isNilOrEmpty,
} from "@tastyworks/tastyworks-api"
import {
  derived,
  get as svelte_get,
  writable,
  type Readable,
  type Writable,
} from "svelte/store"

export const ONE_HUNDRED = 100

interface AddOptions {
  shouldSplitPercentages?: boolean
}
export interface AccountBeneficiaryStore {
  add: (beneficiary: AccountBeneficiary, options?: AddOptions) => void
  edit: (beneficiary: AccountBeneficiary) => void
  remove: (beneficiary: AccountBeneficiary) => void
  set: (beneficiaries: AccountBeneficiary[]) => void
  save: (applicationFormAccessor: ApplicationFormAccessor) => void
  splitPercentages: (beneficiaries: AccountBeneficiary[]) => void
  isPrimary: (beneficiary: AccountBeneficiary) => boolean
  isContingent: (beneficiary: AccountBeneficiary) => boolean
  items: Readable<AccountBeneficiary[]>
  hasBeneficiaries: Readable<boolean>
  primaryBeneficiaries: Readable<AccountBeneficiary[]>
  contingentBeneficiaries: Readable<AccountBeneficiary[]>
  primaryTotalPercentage: Readable<number>
  contingentTotalPercentage: Readable<number>
  editingBeneficiary: Writable<AccountBeneficiary | null>
}

export function createAccountBeneficiaries(): AccountBeneficiaryStore {
  const accountBeneficiaries = writable<AccountBeneficiary[]>([])
  const editingBeneficiary = writable<AccountBeneficiary | null>(null)

  const hasBeneficiaries = derived(
    accountBeneficiaries,
    ($accountBeneficiaries) => !isNilOrEmpty($accountBeneficiaries)
  )

  const primaryBeneficiaries = derived(
    accountBeneficiaries,
    ($accountBeneficiaries) => {
      return $accountBeneficiaries.filter((beneficiary) =>
        isPrimary(beneficiary)
      )
    }
  )

  const contingentBeneficiaries = derived(
    accountBeneficiaries,
    ($accountBeneficiaries) => {
      return $accountBeneficiaries.filter((beneficiary) =>
        isContingent(beneficiary)
      )
    }
  )

  const primaryTotalPercentage = derived(
    primaryBeneficiaries,
    ($primaryBeneficiaries) => {
      return $primaryBeneficiaries.reduce(
        (sum, currentValue) => sum + (currentValue.beneficiaryPercent || 0),
        0
      )
    }
  )

  const contingentTotalPercentage = derived(
    contingentBeneficiaries,
    ($contingentBeneficiaries) => {
      return $contingentBeneficiaries.reduce(
        (sum, currentValue) => sum + (currentValue.beneficiaryPercent || 0),
        0
      )
    }
  )

  // Functions
  function isPrimary(beneficiary: AccountBeneficiary) {
    return beneficiary.beneficiaryType === ACCOUNT_BENEFICIARY_TYPES.PRIMARY
  }

  function isContingent(beneficiary: AccountBeneficiary) {
    return beneficiary.beneficiaryType === ACCOUNT_BENEFICIARY_TYPES.CONTINGENT
  }

  function add(
    accountBeneficiary: AccountBeneficiary,
    options: AddOptions = {
      shouldSplitPercentages: false,
    }
  ) {
    if (
      svelte_get(accountBeneficiaries).find(
        (e) =>
          e.taxNumber === accountBeneficiary.taxNumber &&
          e.firstName === accountBeneficiary.firstName &&
          e.lastName === accountBeneficiary.lastName
      )
    ) {
      return
    }

    accountBeneficiaries.update((beneficiaries) => {
      beneficiaries.push(accountBeneficiary)
      return beneficiaries
    })

    if (options.shouldSplitPercentages) {
      splitPercentages(
        svelte_get(
          isPrimary(accountBeneficiary)
            ? primaryBeneficiaries
            : contingentBeneficiaries
        )
      )
    }
  }

  function edit(beneficiaryData: AccountBeneficiary) {
    const $editingBeneficiary = svelte_get(editingBeneficiary)
    if (!$editingBeneficiary) throw new Error("Missing editing beneficiary")

    Object.assign($editingBeneficiary, beneficiaryData)
    const index = getBeneficiaryIndex($editingBeneficiary)
    accountBeneficiaries.update((beneficiaries) => {
      beneficiaries[index] = $editingBeneficiary
      return beneficiaries
    })
  }

  function remove(accountBeneficiary: AccountBeneficiary) {
    accountBeneficiaries.update((beneficiaries) => {
      return beneficiaries.filter(
        (value: AccountBeneficiary) =>
          accountBeneficiary.taxNumber !== value.taxNumber
      )
    })
  }

  function set(newBeneficiaries: AccountBeneficiary[]) {
    accountBeneficiaries.update((beneficiaries) => {
      beneficiaries = newBeneficiaries
      return beneficiaries
    })
  }

  function splitPercentages(beneficiaries: AccountBeneficiary[]) {
    if (beneficiaries.length < 1) {
      return beneficiaries
    }
    const beneficiaryType = beneficiaries[0].beneficiaryType

    const splitPercent = Math.floor(ONE_HUNDRED / beneficiaries.length)

    accountBeneficiaries.update((beneficiaries) => {
      beneficiaries.forEach((beneficiary: AccountBeneficiary) => {
        if (beneficiary.beneficiaryType === beneficiaryType) {
          beneficiary.beneficiaryPercent = splitPercent
        }
      })
      return beneficiaries
    })

    const sum = isPrimary(beneficiaries[0])
      ? svelte_get(primaryTotalPercentage)
      : svelte_get(contingentTotalPercentage)
    const remainder = ONE_HUNDRED - sum

    if (remainder > 0) {
      const lastBeneficiary = beneficiaries.pop()

      if (lastBeneficiary) {
        editingBeneficiary.set(lastBeneficiary)

        lastBeneficiary.beneficiaryPercent =
          (lastBeneficiary.beneficiaryPercent || 0) + remainder
        edit(lastBeneficiary)

        editingBeneficiary.set(null)
      }
    }
  }

  function getBeneficiaryIndex(accountBeneficiary: AccountBeneficiary) {
    return svelte_get(accountBeneficiaries).indexOf(accountBeneficiary)
  }

  function save(applicationFormAccessor: ApplicationFormAccessor) {
    applicationFormAccessor.batch((accessor) => {
      accessor.primaryBeneficiaries = svelte_get(
        primaryBeneficiaries
      ) as AccountBeneficiaryFormData[]
      accessor.contingentBeneficiaries = svelte_get(
        contingentBeneficiaries
      ) as AccountBeneficiaryFormData[]
    })
  }

  return {
    add,
    contingentBeneficiaries,
    contingentTotalPercentage,
    edit,
    hasBeneficiaries,
    isPrimary,
    isContingent,
    items: accountBeneficiaries,
    primaryBeneficiaries,
    primaryTotalPercentage,
    remove,
    set,
    splitPercentages,
    save,
    editingBeneficiary,
  }
}

/**
 * Converts form data to the @tastyworks/api model representation.
 * @param applicationFormAccessor The form accessor containing beneficiary data.
 * @returns An array of beneficiary models.
 */
export function adaptBeneficiariesFormData(
  applicationFormAccessor: ApplicationFormAccessor
) {
  const primary = applicationFormAccessor.primaryBeneficiaries || []
  const contingent = applicationFormAccessor.contingentBeneficiaries || []

  return [primary, contingent].flat().map((formData) => {
    const accountBeneficiary = accountBeneficiaryAdapter.toModel(
      formData,
      new AccountBeneficiary()
    )
    accountBeneficiary.id = null as unknown as string // Remove Id for application POST
    return accountBeneficiary
  })
}

/**
 * Clears splitBeneficiaryInformation in applicationFormAccessor by resetting
 * beneficiary form fields to their default values from the schemas.
 * @param applicationFormAccessor The form accessor to update.
 */
export function clearSplitBeneficiaryInfo(
  applicationFormAccessor: ApplicationFormAccessor
) {
  applicationFormAccessor.save(
    beneficiaryNameSchema.getDefault(),
    "account.addBeneficiaries.beneficiaryName"
  )
  applicationFormAccessor.save(
    beneficiaryDetailsSchema.getDefault(),
    "account.addBeneficiaries.beneficiaryDetails"
  )
  applicationFormAccessor.save(
    beneficiaryAddressSchema.getDefault(),
    "account.addBeneficiaries.beneficiaryAddress"
  )
}

export function calculateDefaultBeneficiaryPercentage(
  percentage: number
): string {
  // when adding a new beneficiary, set the percentage to the remainder of 100% minus the total percentage of existing beneficiaries, with a minimum of 1%
  return String(
    Math.max(1, calculatePercentageRemainder(ONE_HUNDRED, percentage))
  )
}
